import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { DashboardLayout } from '../layouts/DashboardLayout';
import { 
  Bell, Check, Trash2, Mail, Phone, Volume2, Monitor, Filter
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

export default function NotificationsPage() {
  const { api } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/notifications');
      setNotifications(response.data);
    } catch (error) {
      toast.error('Failed to fetch notifications');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const handleMarkRead = async (notificationId) => {
    try {
      await api.post(`/notifications/${notificationId}/read`);
      setNotifications(notifications.map(n => 
        n.id === notificationId ? { ...n, read: true } : n
      ));
    } catch (error) {
      toast.error('Failed to mark as read');
    }
  };

  const handleMarkAllRead = async () => {
    try {
      for (const notif of notifications.filter(n => !n.read)) {
        await api.post(`/notifications/${notif.id}/read`);
      }
      setNotifications(notifications.map(n => ({ ...n, read: true })));
      toast.success('All notifications marked as read');
    } catch (error) {
      toast.error('Failed to mark all as read');
    }
  };

  const getChannelIcon = (channel) => {
    const icons = {
      browser: Monitor,
      email: Mail,
      sms: Phone,
      voice: Volume2
    };
    return icons[channel] || Bell;
  };

  const getTypeColor = (type) => {
    const colors = {
      shift_reminder: 'bg-[#F59E0B]/20 text-[#F59E0B]',
      workload: 'bg-[#4B167A]/40 text-white',
      accolade: 'bg-[#D4AF37]/20 text-[#D4AF37]',
      system: 'bg-[#1F1B71] text-white'
    };
    return colors[type] || 'bg-white/10 text-white';
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const filteredNotifications = notifications.filter(n => {
    if (filter === 'unread') return !n.read;
    if (filter === 'read') return n.read;
    return true;
  });

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="notifications-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
              <span className="text-[#D4AF37]">The Pulse</span> Notifications
            </h1>
            <p className="text-white/50 mt-1">
              {unreadCount > 0 ? `${unreadCount} unread notification${unreadCount > 1 ? 's' : ''}` : 'All caught up!'}
            </p>
          </div>
          <div className="flex gap-3">
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-32 bg-[#0A091E]/50 border-white/20 text-white" data-testid="select-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#1F1B71] border-white/10 text-white">
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="unread">Unread</SelectItem>
                <SelectItem value="read">Read</SelectItem>
              </SelectContent>
            </Select>
            {unreadCount > 0 && (
              <Button
                onClick={handleMarkAllRead}
                variant="outline"
                className="border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10"
                data-testid="btn-mark-all-read"
              >
                <Check className="w-4 h-4 mr-2" />
                Mark All Read
              </Button>
            )}
          </div>
        </div>

        {/* Notification Channels Info */}
        <Card className="glass-card border-0">
          <CardContent className="pt-6">
            <div className="flex flex-wrap gap-4 justify-center">
              {[
                { icon: Monitor, label: 'Browser', color: '#D4AF37' },
                { icon: Mail, label: 'Email', color: '#10B981' },
                { icon: Phone, label: 'SMS', color: '#4B167A' },
                { icon: Volume2, label: 'Voice', color: '#F59E0B' }
              ].map((channel) => (
                <div key={channel.label} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#1F1B71]/30">
                  <channel.icon className="w-4 h-4" style={{ color: channel.color }} />
                  <span className="text-white/70 text-sm">{channel.label}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Notifications List */}
        <Card className="glass-card border-0">
          <CardHeader className="border-b border-white/10">
            <CardTitle className="flex items-center gap-2 text-white">
              <Bell className="w-5 h-5 text-[#D4AF37]" />
              Notifications
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-white/50">Loading notifications...</div>
            ) : filteredNotifications.length === 0 ? (
              <div className="p-8 text-center">
                <Bell className="w-12 h-12 text-white/20 mx-auto mb-4" />
                <p className="text-white/50">
                  {filter === 'all' ? 'No notifications yet' : `No ${filter} notifications`}
                </p>
              </div>
            ) : (
              <div className="divide-y divide-white/10">
                {filteredNotifications.map((notification) => (
                  <div
                    key={notification.id}
                    className={`p-4 flex items-start gap-4 hover:bg-white/5 ${!notification.read ? 'bg-[#D4AF37]/5' : ''}`}
                    data-testid={`notification-${notification.id}`}
                  >
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${!notification.read ? 'bg-[#D4AF37]/20' : 'bg-[#1F1B71]'}`}>
                      <Bell className={`w-5 h-5 ${!notification.read ? 'text-[#D4AF37]' : 'text-white/50'}`} />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className={`font-semibold ${!notification.read ? 'text-white' : 'text-white/70'}`}>
                          {notification.title}
                        </h4>
                        <Badge className={getTypeColor(notification.notification_type)}>
                          {notification.notification_type.replace('_', ' ')}
                        </Badge>
                      </div>
                      
                      <p className="text-white/50 text-sm mb-2">{notification.message}</p>
                      
                      <div className="flex items-center gap-4">
                        <span className="text-white/30 text-xs">{formatDate(notification.created_at)}</span>
                        <div className="flex items-center gap-1">
                          {notification.channels.map((channel) => {
                            const Icon = getChannelIcon(channel);
                            return <Icon key={channel} className="w-3 h-3 text-white/30" />;
                          })}
                        </div>
                      </div>
                    </div>

                    {!notification.read && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleMarkRead(notification.id)}
                        className="text-[#D4AF37] hover:text-[#D4AF37]/80 hover:bg-white/5"
                        data-testid={`btn-mark-read-${notification.id}`}
                      >
                        <Check className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
